import React, { useEffect, useState } from 'react'
import { Switch } from '@Components/shadcn/ui/switch'
import { Tooltip } from 'react-tooltip';
import { RootState, useAppSelector } from '@Store/store';

interface ButtonText {
    title: string;
    type?: string;
    data?: string;
}

export interface OpeningMessageData {
    enabled: boolean;
    text?: string;
    buttonText?: ButtonText[];
}

interface ResponseDataState {
    opening_message: OpeningMessageData;
    data: any[];
}

interface OpeningMessageProps {
    data: OpeningMessageData;
    UpdateOpeningMessage: (data: OpeningMessageData) => void;
    automationId: number|string|undefined;
}

function OpeningMessage({ data, UpdateOpeningMessage, automationId }: OpeningMessageProps) {

    const { user } = useAppSelector((state: RootState) => state.profile);
    
    const defaultOpeningMessage = {
        enabled: false,
        text: "Hey there!\n\nI'm so happy you're here, thank you so much for your interest 😊\n\nClick below and I'll send you the link in just a sec ✨",
        buttonText: [{
            title: "Send me the link",
            postback_text: `OpeningMessage#${user?.user_id}#${automationId}#-1`,
            type: "postback" as const
        }]
    }

    // Initialize with response data if available, otherwise use defaults
    const [isToggleOn, setIsToggleOn] = useState(false)
    const [openingMessage, setOpeningMessage] = useState<OpeningMessageData>(defaultOpeningMessage)
    const [isEditing, setIsEditing] = useState(false)
    const [editText, setEditText] = useState("")
    const [editButtonTitle, setEditButtonTitle] = useState("")


    useEffect(() => {
      setOpeningMessage(data)
      setIsToggleOn(data?.enabled || false)
    }, [data])
    
    


    const handleEdit = () => {
        setEditText(openingMessage?.text || defaultOpeningMessage.text)
        setEditButtonTitle(openingMessage?.buttonText?.[0]?.title || defaultOpeningMessage.buttonText[0].title)
        setIsEditing(true)
    }

    const handleSave = () => {
        const temp_om = {
            ...openingMessage,
            enabled: isToggleOn,
            text: editText,
            buttonText: [{
                ...openingMessage?.buttonText?.[0] || defaultOpeningMessage.buttonText[0],
                title: editButtonTitle
            }]
        }
        setOpeningMessage(temp_om)
        setIsEditing(false)
        UpdateOpeningMessage(temp_om)
    }

    const handleCancel = () => {
        setEditText("")
        setEditButtonTitle("")
        setIsEditing(false)
    }

    useEffect(() => {
        if (isToggleOn) {
            // When toggle is ON, use current data or defaults
            const temp = {...openingMessage}
            temp.enabled = true
            setOpeningMessage(temp)
            UpdateOpeningMessage(temp)
        } else {
            // When toggle is OFF, reset to default values
            const defaultData = {
                enabled: false,
                text: defaultOpeningMessage.text,
                buttonText: defaultOpeningMessage.buttonText
            }
            setOpeningMessage(defaultData)
            UpdateOpeningMessage(defaultData)
        }
    }, [isToggleOn])



    return (
        <div className="w-full mt-2 mb-3">
            {/* Toggle Bar */}
            <div className=" border flex items-center justify-between p-4 bg-gray-100 rounded-lg">
                <span className="text-sm font-medium text-gray-700 align-middle flex gap-1">
                    Opening Message
                    <button
                        className="text-gray-400 hover:text-gray-600 opening-message-tooltip"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </button>
                    <Tooltip anchorSelect=".opening-message-tooltip" place="top">
                        <span className='text-center'>The opening message is the first message<br/> your followers will see when they <br/> interact with your automation.</span>
                    </Tooltip>
                </span>
                <Switch
                    checked={isToggleOn}
                    onCheckedChange={setIsToggleOn}
                />
            </div>

            {/* Collapsible Content */}
            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isToggleOn ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0' }`} >
                <div className=" bg-white rounded-[20px] md:p-4 p-2">
                    <div className={`max-w-[400px] mx-auto rounded-[20px] border p-6 relative ${!isToggleOn ? 'opacity-50' : ''}`}>
                        {/* Edit Button */}
                        {!isEditing && (
                            <button
                                onClick={handleEdit}
                                className="absolute flex items-center gap-1 top-1 right-1 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                title="Edit message" > 
                            Edit
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                            </button>
                        )}

                        {!isEditing ? (
                            <>
                                <div className="text-sm text-gray-600 whitespace-pre-wrap">
                                    {openingMessage?.text || defaultOpeningMessage.text}
                                </div>
                                {(openingMessage?.buttonText || defaultOpeningMessage.buttonText)?.map((btn, idx) => (
                                    <div key={idx} className="py-2 px-4 border rounded-xl text-center text-sm font-medium text-[#3535d5]">
                                        {btn.title}
                                    </div>
                                ))}
                            </>
                        ) : (
                            <div className="">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Message Text
                                    </label>
                                    <textarea
                                        value={editText}
                                        onChange={(e) => setEditText(e.target.value)}
                                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 ring-none outline-none focus:border-none text-sm resize-none"
                                        rows={7}
                                        placeholder="Enter your opening message..."
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Button Title
                                    </label>
                                    <input
                                        type="text"
                                        value={editButtonTitle}
                                        maxLength={20}
                                        onChange={(e) => setEditButtonTitle(e.target.value)}
                                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 ring-none outline-none focus:border-none text-sm"
                                        placeholder="Enter button text..."
                                    />
                                </div>
                                <div className="flex gap-2 justify-end mt-2">
                                    <button
                                        onClick={handleCancel}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleSave}
                                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                                    >
                                        Save
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default OpeningMessage